import type { Product, CartItemRead, OrderRead, OrderItemRead } from '@shared/types'

// Extend window object for GTM dataLayer
declare global {
  interface Window {
    dataLayer: any[]
    gtag: (...args: any[]) => void
  }
}

// GA4 Enhanced Ecommerce Item interface
interface GA4Item {
  item_id: string
  item_name: string
  item_category?: string
  item_category2?: string
  price: number
  quantity: number
  currency?: string
  item_brand?: string
}

// Convert product to GA4 item format
function productToGA4Item(product: Product, quantity: number = 1): GA4Item {
  return {
    item_id: product.id,
    item_name: product.title,
    item_category: product.product_type || 'Book',
    item_category2: product.more_info?.author?.name || '',
    price: product.price || 0,
    quantity,
    currency: 'ILS',
    item_brand: 'Sela Meir'
  }
}

// Convert cart item to GA4 item format
function cartItemToGA4Item(cartItem: CartItemRead): GA4Item {
  return productToGA4Item(cartItem.product, cartItem.quantity)
}

// Push event to dataLayer
function pushToDataLayer(eventData: any) {
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push(eventData)
    console.log('GA4 Event:', eventData)
  }
}

// Track when user views an item
export function trackViewItem(product: Product) {
  pushToDataLayer({
    event: 'view_item',
    ecommerce: {
      currency: 'ILS',
      value: product.price || 0,
      items: [productToGA4Item(product)]
    }
  })
}

// Track when user adds item to cart
export function trackAddToCart(product: Product, quantity: number = 1) {
  pushToDataLayer({
    event: 'add_to_cart',
    ecommerce: {
      currency: 'ILS',
      value: (product.price || 0) * quantity,
      items: [productToGA4Item(product, quantity)]
    }
  })
}

// Track when user removes item from cart
export function trackRemoveFromCart(product: Product, quantity: number = 1) {
  pushToDataLayer({
    event: 'remove_from_cart',
    ecommerce: {
      currency: 'ILS',
      value: (product.price || 0) * quantity,
      items: [productToGA4Item(product, quantity)]
    }
  })
}

// Track when user views cart
export function trackViewCart(cartItems: CartItemRead[], total: number) {
  pushToDataLayer({
    event: 'view_cart',
    ecommerce: {
      currency: 'ILS',
      value: total,
      items: cartItems.map(cartItemToGA4Item)
    }
  })
}

// Track when user begins checkout
export function trackBeginCheckout(cartItems: CartItemRead[], total: number) {
  pushToDataLayer({
    event: 'begin_checkout',
    ecommerce: {
      currency: 'ILS',
      value: total,
      items: cartItems.map(cartItemToGA4Item)
    }
  })
}

// Track when user adds shipping info
export function trackAddShippingInfo(cartItems: CartItemRead[], total: number, shippingTier?: string) {
  pushToDataLayer({
    event: 'add_shipping_info',
    ecommerce: {
      currency: 'ILS',
      value: total,
      shipping_tier: shippingTier || 'Standard',
      items: cartItems.map(cartItemToGA4Item)
    }
  })
}

// Track when user adds payment info
export function trackAddPaymentInfo(cartItems: CartItemRead[], total: number, paymentType?: string) {
  pushToDataLayer({
    event: 'add_payment_info',
    ecommerce: {
      currency: 'ILS',
      value: total,
      payment_type: paymentType || 'Credit Card',
      items: cartItems.map(cartItemToGA4Item)
    }
  })
}

interface PurchaseEvent {
  order_id: string
  value: number
  currency: string
}

function orderItemToGA4Item(orderItem: OrderItemRead): GA4Item {
  return {
    item_id: orderItem.product_id,
    item_name: orderItem.product_title || '',
    price: orderItem.unit_price || 0,
    quantity: orderItem.quantity || 1,
    currency: 'ILS',
    item_brand: 'Sela Meir'
  }
}

// Track successful purchase
export function trackPurchase(order: OrderRead) {

  pushToDataLayer({
    event: 'purchase',
    ecommerce: {
      transaction_id: order.id,
      value: order.total,
      currency: 'ILS',
      items: order.items?.map(orderItemToGA4Item) || []
    }
  })
}

// Track search
export function trackSearch(searchTerm: string, resultCount?: number) {
  pushToDataLayer({
    event: 'search',
    search_term: searchTerm,
    search_results: resultCount
  })
}

// Track when user selects an item from search/category
export function trackSelectItem(product: Product, listName?: string, listId?: string, index?: number) {
  pushToDataLayer({
    event: 'select_item',
    item_list_name: listName || 'Search Results',
    item_list_id: listId || 'search_results',
    ecommerce: {
      items: [{
        ...productToGA4Item(product),
        index: index || 0
      }]
    }
  })
}

// Track when user views a list of items
export function trackViewItemList(products: Product[], listName: string, listId?: string) {
  pushToDataLayer({
    event: 'view_item_list',
    item_list_name: listName,
    item_list_id: listId || listName.toLowerCase().replace(/\s+/g, '_'),
    ecommerce: {
      items: products.map((product, index) => ({
        ...productToGA4Item(product),
        index
      }))
    }
  })
}

// Custom event for subscription events
// Note: This is optional - standard add_to_cart events already capture product_type = 'Subscription'
export function trackSubscriptionEvent(eventType: 'subscribe' | 'cancel' | 'renew', product: Product) {
  pushToDataLayer({
    event: 'subscription_event',
    subscription_action: eventType,
    item_id: product.id,
    item_name: product.title,
    value: product.price || 0,
    currency: 'ILS'
  })
}

// Custom event for event ticket views
// Note: Purchases use standard add_to_cart events with product_type = 'Event'
export function trackEventTicket(eventType: 'view' | 'purchase', product: Product) {
  pushToDataLayer({
    event: 'event_ticket',
    ticket_action: eventType,
    item_id: product.id,
    item_name: product.title,
    event_date: product.more_info?.date || '',
    event_location: product.more_info?.location || '',
    value: product.price || 0,
    currency: 'ILS'
  })
} 